// News Service - Aggregating financial news from multiple free sources
import type { NewsItem, NewsCategory } from '../types';

export interface NewsSentiment {
  overall: 'Positive' | 'Negative' | 'Neutral';
  confidence: number;
  positiveCount: number;
  negativeCount: number;
  neutralCount: number;
  totalCount: number;
  lastUpdated: string;
}

// News sources configuration
const NEWS_SOURCES = {
  // RSS feeds (free)
  rss: [
    {
      name: 'Yahoo Finance',
      url: 'https://feeds.finance.yahoo.com/rss/2.0/headline',
      category: 'markets' as const
    },
    {
      name: 'Investing.com',
      url: 'https://www.investing.com/rss/news.rss',
      category: 'markets' as const
    },
    {
      name: 'ForexLive',
      url: 'https://www.forexlive.com/feed/',
      category: 'forex' as const
    },
    {
      name: 'Benzinga',
      url: 'https://www.benzinga.com/feed',
      category: 'markets' as const
    }
  ],
  // Free APIs
  apis: [
    {
      name: 'NewsAPI',
      baseUrl: 'https://newsapi.org/v2/everything',
      sources: 'bloomberg,reuters,financial-times,the-wall-street-journal',
      category: 'breaking' as const
    }
  ]
};

// Sentiment analysis keywords
const SENTIMENT_KEYWORDS = {
  positive: [
    'surge', 'rally', 'gain', 'rise', 'boost', 'jump', 'soar', 'climb',
    'bullish', 'optimistic', 'positive', 'growth', 'profit', 'success',
    'breakthrough', 'record', 'high', 'strong', 'robust', 'solid'
  ],
  negative: [
    'fall', 'drop', 'decline', 'crash', 'plunge', 'tumble', 'slide',
    'bearish', 'pessimistic', 'negative', 'loss', 'deficit', 'concern',
    'worry', 'fear', 'risk', 'weak', 'poor', 'disappointing', 'low'
  ]
};

class NewsService {
  private cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_DURATION = 300000; // 5 minutes cache
  private readonly CORS_PROXY = 'https://api.allorigins.win/raw?url=';
  
  // Circuit breaker pattern to prevent network errors
  private circuitBreaker = {
    failureCount: 0,
    lastFailureTime: 0,
    threshold: 3, // Max failures before opening circuit
    timeout: 600000, // 10 minutes before trying again
    isOpen: false
  };
  
  // Development mode flag - disable external calls to prevent errors
  private readonly DEVELOPMENT_MODE = true; // Set to false in production

  // Analyze sentiment of text
  private analyzeSentiment(text: string): 'positive' | 'negative' | 'neutral' {
    const lowerText = text.toLowerCase();
    let positiveScore = 0;
    let negativeScore = 0;

    SENTIMENT_KEYWORDS.positive.forEach(keyword => {
      if (lowerText.includes(keyword)) positiveScore++;
    });

    SENTIMENT_KEYWORDS.negative.forEach(keyword => {
      if (lowerText.includes(keyword)) negativeScore++;
    });

    if (positiveScore > negativeScore) return 'positive';
    if (negativeScore > positiveScore) return 'negative';
    return 'neutral';
  }

  // Decode HTML entities
  private decodeHtmlEntities(text: string): string {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  // Clean and format text content
  private cleanText(text: string): string {
    if (!text) return '';
    
    // Decode HTML entities
    let cleaned = this.decodeHtmlEntities(text);
    
    // Remove HTML tags
    cleaned = cleaned.replace(/<[^>]*>/g, '');
    
    // Remove extra whitespace and normalize
    cleaned = cleaned.replace(/\s+/g, ' ').trim();
    
    // Remove common RSS artifacts
    cleaned = cleaned.replace(/\[CDATA\[|\]\]/g, '');
    
    return cleaned;
  }

  // Check circuit breaker status
  private isCircuitOpen(): boolean {
    if (this.circuitBreaker.isOpen) {
      const timeSinceLastFailure = Date.now() - this.circuitBreaker.lastFailureTime;
      if (timeSinceLastFailure > this.circuitBreaker.timeout) {
        // Reset circuit breaker after timeout
        this.circuitBreaker.isOpen = false;
        this.circuitBreaker.failureCount = 0;
        console.log('Circuit breaker reset - attempting external calls again');
        return false;
      }
      return true;
    }
    return false;
  }
  
  // Record circuit breaker failure
  private recordFailure(): void {
    this.circuitBreaker.failureCount++;
    this.circuitBreaker.lastFailureTime = Date.now();
    
    if (this.circuitBreaker.failureCount >= this.circuitBreaker.threshold) {
      this.circuitBreaker.isOpen = true;
      console.log(`Circuit breaker opened after ${this.circuitBreaker.failureCount} failures - using mock data only`);
    }
  }

  // Parse RSS feed with enhanced error handling
  private async parseRSSFeed(url: string, source: string, category: NewsCategory): Promise<NewsItem[]> {
    // Skip external calls in development mode or if circuit breaker is open
    if (this.DEVELOPMENT_MODE || this.isCircuitOpen()) {
      console.log(`Skipping external RSS call for ${source} (development mode or circuit breaker open)`);
      return [];
    }
    
    try {
      // Add timeout and better error handling
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(`${this.CORS_PROXY}${encodeURIComponent(url)}`, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/rss+xml, application/xml, text/xml',
          'User-Agent': 'Mozilla/5.0 (compatible; NewsReader/1.0)'
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`RSS fetch failed: ${response.status} ${response.statusText}`);
      }
      
      const xmlText = await response.text();
      if (!xmlText || xmlText.trim().length === 0) {
        throw new Error('Empty response from RSS feed');
      }
      
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
      
      // Check for XML parsing errors
      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        throw new Error('XML parsing error: ' + parseError.textContent);
      }
      
      const items = xmlDoc.querySelectorAll('item');
      const newsItems: NewsItem[] = [];

      items.forEach((item, index) => {
        const rawTitle = item.querySelector('title')?.textContent || '';
        const rawDescription = item.querySelector('description')?.textContent || '';
        const link = item.querySelector('link')?.textContent || '';
        const pubDate = item.querySelector('pubDate')?.textContent || new Date().toISOString();
        
        // Clean the text content
        const title = this.cleanText(rawTitle);
        const description = this.cleanText(rawDescription);
        
        if (title && link) {
          const sentiment = this.analyzeSentiment(title + ' ' + description);
          
          // Create a proper summary
          let summary = description;
          if (summary.length > 150) {
            summary = summary.substring(0, 150).trim();
            // Find the last complete word
            const lastSpace = summary.lastIndexOf(' ');
            if (lastSpace > 100) {
              summary = summary.substring(0, lastSpace);
            }
            summary += '...';
          }
          
          newsItems.push({
            id: `${source}-${index}-${Date.now()}`,
            title: title,
            summary: summary || title.substring(0, 100) + '...',
            url: link.trim(),
            source,
            category,
            sentiment,
            affectedAssets: this.extractAffectedAssets(title + ' ' + (summary || '')),
            publishedAt: new Date(pubDate).toISOString()
          });
        }
      });

      return newsItems.slice(0, 10); // Limit to 10 items per source
    } catch (error) {
      // Record failure for circuit breaker
      this.recordFailure();
      
      // Enhanced error logging with more context
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          console.warn(`RSS feed timeout for ${source}:`, url);
        } else if (error.message.includes('net::ERR_FAILED')) {
          console.warn(`Network error for ${source} (CORS/Network issue):`, url);
        } else {
          console.warn(`Error parsing RSS feed for ${source}:`, error.message);
        }
      } else {
        console.warn(`Unknown error parsing RSS feed for ${source}:`, error);
      }
      return [];
    }
  }

  // Extract affected assets from text
  private extractAffectedAssets(text: string): string[] {
    const assets: string[] = [];
    const lowerText = text.toLowerCase();
    
    const assetKeywords = {
      'GOLD': ['gold', 'precious metals', 'xau'],
      'SILVER': ['silver', 'xag'],
      'CRUDE_OIL': ['oil', 'crude', 'wti', 'brent'],
      'GASOLINE': ['gasoline', 'gas', 'rbob'],
      'COPPER': ['copper', 'hg'],
      'BTC': ['bitcoin', 'btc'],
      'ETH': ['ethereum', 'eth'],
      'USD': ['dollar', 'usd', 'dxy'],
      'EUR': ['euro', 'eur'],
      'NASDAQ': ['nasdaq', 'tech stocks'],
      'SPX': ['s&p 500', 'spx', 'spy'],
      'BONDS': ['bonds', 'treasury', 'yield']
    };

    Object.entries(assetKeywords).forEach(([asset, keywords]) => {
      if (keywords.some(keyword => lowerText.includes(keyword))) {
        assets.push(asset);
      }
    });

    return assets.length > 0 ? assets : ['GENERAL'];
  }

  // Extract relevant tags from text
  private extractTags(text: string): string[] {
    const tags: string[] = [];
    const lowerText = text.toLowerCase();
    
    const tagKeywords = {
      'Bitcoin': ['bitcoin', 'btc'],
      'Ethereum': ['ethereum', 'eth'],
      'Gold': ['gold', 'precious metals'],
      'Oil': ['oil', 'crude', 'energy'],
      'Fed': ['fed', 'federal reserve', 'interest rate'],
      'Inflation': ['inflation', 'cpi', 'consumer price'],
      'Earnings': ['earnings', 'quarterly', 'revenue'],
      'IPO': ['ipo', 'initial public offering'],
      'Merger': ['merger', 'acquisition', 'm&a'],
      'Crypto': ['crypto', 'cryptocurrency', 'blockchain']
    };

    Object.entries(tagKeywords).forEach(([tag, keywords]) => {
      if (keywords.some(keyword => lowerText.includes(keyword))) {
        tags.push(tag);
      }
    });

    return tags;
  }

  // Get mock news data as fallback
  private getMockNewsData(): NewsItem[] {
    return [
      {
        id: 'mock-1',
        title: 'Gold Futures Surge to Monthly Highs Amid Market Uncertainty',
        summary: 'Gold prices climbed 2.1% today as investors seek safe-haven assets amid global economic concerns and rising inflation expectations.',
        url: 'https://www.bloomberg.com/news/articles/2024-01-15/gold-futures-surge-monthly-highs',
        source: 'Bloomberg',
        category: 'markets',
        sentiment: 'positive',
        affectedAssets: ['GOLD', 'SILVER'],
        publishedAt: new Date(Date.now() - 3600000).toISOString()
      },
      {
        id: 'mock-2',
        title: 'Federal Reserve Signals Potential Rate Cuts in 2024',
        summary: 'Fed officials hint at possible monetary policy adjustments as inflation shows signs of cooling and economic growth moderates.',
        url: 'https://www.reuters.com/markets/us/federal-reserve-signals-rate-cuts-2024',
        source: 'Reuters',
        category: 'economics',
        sentiment: 'positive',
        affectedAssets: ['USD', 'BONDS'],
        publishedAt: new Date(Date.now() - 7200000).toISOString()
      },
      {
        id: 'mock-3',
        title: 'Tech Stocks Rally Continues as AI Optimism Grows',
        summary: 'Major technology companies see continued gains as artificial intelligence investments drive growth expectations and market confidence.',
        url: 'https://www.wsj.com/articles/tech-stocks-rally-ai-optimism-grows',
        source: 'Wall Street Journal',
        category: 'markets',
        sentiment: 'positive',
        affectedAssets: ['NASDAQ', 'TECH'],
        publishedAt: new Date(Date.now() - 10800000).toISOString()
      },
      {
        id: 'mock-4',
        title: 'Bitcoin Volatility Increases Ahead of ETF Decision',
        summary: 'Cryptocurrency markets show increased volatility as regulators prepare to announce ETF approvals, creating uncertainty among traders.',
        url: 'https://www.benzinga.com/markets/cryptocurrency/24/01/bitcoin-volatility-etf-decision',
        source: 'Benzinga',
        category: 'crypto',
        sentiment: 'neutral',
        affectedAssets: ['BTC', 'ETH'],
        publishedAt: new Date(Date.now() - 14400000).toISOString()
      },
      {
        id: 'mock-5',
        title: 'Oil Prices Drop on Increased Production Forecasts',
        summary: 'Crude oil futures decline as OPEC+ members signal potential production increases in coming months, pressuring energy markets.',
        url: 'https://www.forexlive.com/news/oil-prices-drop-production-forecasts',
        source: 'ForexLive',
        category: 'markets',
        sentiment: 'negative',
        affectedAssets: ['CRUDE_OIL', 'GASOLINE'],
        publishedAt: new Date(Date.now() - 18000000).toISOString()
      },
      {
        id: 'mock-6',
        title: 'European Markets Close Higher on Strong Earnings',
        summary: 'European indices end the session in positive territory following better-than-expected corporate earnings and improved economic outlook.',
        url: 'https://www.ft.com/content/european-markets-close-higher-earnings',
        source: 'Financial Times',
        category: 'markets',
        sentiment: 'positive',
        affectedAssets: ['EUROSTOXX', 'EUR'],
        publishedAt: new Date(Date.now() - 21600000).toISOString()
      }
    ];
  }

  // Fetch news from all sources with enhanced error handling
  async fetchNews(): Promise<NewsItem[]> {
    console.log('🔧 fetchNews called, DEVELOPMENT_MODE:', this.DEVELOPMENT_MODE);
    
    const cacheKey = 'financial_news';
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      console.log('🔧 Returning cached data:', cached.data.length, 'articles');
      return cached.data;
    }

    // In development mode, immediately return mock data to prevent network errors
    if (this.DEVELOPMENT_MODE) {
      console.log('🔧 Development mode: Using mock news data immediately');
      const mockNews = this.getMockNewsData();
      console.log('🔧 Mock news generated:', mockNews.length, 'articles');
      console.log('🔧 First mock article:', mockNews[0]?.title);
      this.cache.set(cacheKey, { data: mockNews, timestamp: Date.now() });
      return mockNews;
    }

    try {
      const allNews: NewsItem[] = [];
      let successfulSources = 0;
      let totalSources = NEWS_SOURCES.rss.length;
      
      // Fetch from RSS sources with individual error handling
      const rssPromises = NEWS_SOURCES.rss.map(async (source) => {
        try {
          const result = await this.parseRSSFeed(source.url, source.name, source.category);
          if (result.length > 0) {
            successfulSources++;
            console.log(`Successfully fetched ${result.length} articles from ${source.name}`);
          }
          return result;
        } catch (error) {
          console.warn(`Failed to fetch from ${source.name}:`, error);
          return [];
        }
      });
      
      const rssResults = await Promise.allSettled(rssPromises);
      rssResults.forEach(result => {
        if (result.status === 'fulfilled') {
          allNews.push(...result.value);
        }
      });

      console.log(`News fetch summary: ${successfulSources}/${totalSources} sources successful, ${allNews.length} total articles`);

      // Always ensure we have some news to display
      let finalNews: NewsItem[];
      if (allNews.length > 0) {
        finalNews = allNews;
        console.log('Using real news data from external sources');
      } else {
        finalNews = this.getMockNewsData();
        if (this.circuitBreaker.isOpen) {
          console.log('Circuit breaker open: Using mock news data');
        } else {
          console.log('All external sources failed, using mock news data');
        }
      }
      
      // Sort by publication date (newest first)
      finalNews.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
      
      // Limit to 20 most recent articles
      const limitedNews = finalNews.slice(0, 20);
      
      this.cache.set(cacheKey, { data: limitedNews, timestamp: Date.now() });
      return limitedNews;
    } catch (error) {
      console.error('Critical error in fetchNews, falling back to mock data:', error);
      const mockNews = this.getMockNewsData();
      this.cache.set(cacheKey, { data: mockNews, timestamp: Date.now() });
      return mockNews;
    }
  }

  // Calculate overall news sentiment
  async getNewsSentiment(): Promise<NewsSentiment> {
    const news = await this.fetchNews();
    
    const sentimentCounts = news.reduce(
      (acc, item) => {
        const sentiment = item.sentiment?.toLowerCase() || 'neutral';
        acc[sentiment as keyof typeof acc]++;
        return acc;
      },
      { positive: 0, negative: 0, neutral: 0 }
    );

    const total = news.length;
    const positiveRatio = sentimentCounts.positive / total;
    const negativeRatio = sentimentCounts.negative / total;
    
    let overall: 'Positive' | 'Negative' | 'Neutral';
    let confidence: number;
    
    if (positiveRatio > 0.6) {
      overall = 'Positive';
      confidence = Math.min(95, positiveRatio * 100);
    } else if (negativeRatio > 0.6) {
      overall = 'Negative';
      confidence = Math.min(95, negativeRatio * 100);
    } else {
      overall = 'Neutral';
      confidence = Math.max(50, (1 - Math.abs(positiveRatio - negativeRatio)) * 100);
    }

    return {
      overall,
      confidence,
      positiveCount: sentimentCounts.positive,
      negativeCount: sentimentCounts.negative,
      neutralCount: sentimentCounts.neutral,
      totalCount: total,
      lastUpdated: new Date().toISOString()
    };
  }

  // Get breaking news (high priority)
  async getBreakingNews(): Promise<NewsItem[]> {
    const allNews = await this.fetchNews();
    return allNews
      .filter(item => 
        item.category === 'breaking' || 
        item.title.toLowerCase().includes('fed') ||
        item.title.toLowerCase().includes('bitcoin') ||
        item.title.toLowerCase().includes('gold')
      )
      .slice(0, 5);
  }

  // Get news by category
  async getNewsByCategory(category: NewsCategory): Promise<NewsItem[]> {
    const allNews = await this.fetchNews();
    return allNews.filter(item => item.category === category);
  }

  // Start real-time news updates
  startNewsUpdates(callback: (news: NewsItem[]) => void, interval = 300000): () => void {
    const updateNews = async () => {
      const news = await this.fetchNews();
      callback(news);
    };

    // Initial load
    updateNews();
    
    // Set up interval (5 minutes)
    const intervalId = setInterval(updateNews, interval);
    
    // Return cleanup function
    return () => {
      clearInterval(intervalId);
      console.log('News real-time updates stopped');
    };
  }

  // Stop news updates
  stopNewsUpdates(intervalId: NodeJS.Timeout) {
    clearInterval(intervalId);
  }
}

export const newsService = new NewsService();
export default newsService;